// ── Action taxonomy display — single source ──────────────────────────────────
// Extracted from ActivityLog.tsx (ob-A5, audit-control-observatory) so the
// new Trace View can reuse the exact same action → { label, color } mapping
// instead of growing a second copy that drifts. namespace.verb actions not
// listed here fall back to a readable default — see getActionMeta below.

export interface ActionMeta {
  label: string;
  color: string; // 'text-X bg-Y' tailwind pair, matching this file's palette
}

export const ACTION_LABELS: Record<string, ActionMeta> = {
  'user.player_submit': { label: 'Player submit', color: 'text-blue-400 bg-blue-500/10' },
  'admin.move_clan_member': { label: 'Move member', color: 'text-violet-400 bg-violet-500/10' },
  'admin.remove_clan_member': { label: 'Remove member', color: 'text-red-400 bg-red-500/10' },
  'admin.force_claim_seat': { label: 'Force claim', color: 'text-amber-400 bg-amber-500/10' },
  'admin.swap_seat': { label: 'Swap seat', color: 'text-amber-400 bg-amber-500/10' },
  'admin.grant_org_credits': { label: 'Grant credits', color: 'text-green-400 bg-green-500/10' },
  'admin.grant_platform_credits': { label: 'Platform grant', color: 'text-green-400 bg-green-500/10' },
  'admin.update_spend_cap': { label: 'Update cap', color: 'text-gray-400 bg-gray-500/10' },
  'admin.transfer_leadership': { label: 'Transfer lead', color: 'text-yellow-400 bg-yellow-500/10' },
  'admin.bulk_import': { label: 'Bulk import', color: 'text-violet-400 bg-violet-500/10' },
  'admin.activate_competition': { label: 'Activate comp', color: 'text-green-400 bg-green-500/10' },
  'admin.end_competition': { label: 'End comp', color: 'text-red-400 bg-red-500/10' },
  'admin.advance_stage': { label: 'Advance stage', color: 'text-violet-400 bg-violet-500/10' },
  'admin.simulate_tournament': { label: 'Simulate', color: 'text-cyan-400 bg-cyan-500/10' },
  'admin.delete_simulated_tournament': { label: 'Delete sim', color: 'text-red-400 bg-red-500/10' },
  'admin.flag_approve': { label: 'Flag approve', color: 'text-green-400 bg-green-500/10' },
  'admin.flag_reject': { label: 'Flag reject', color: 'text-red-400 bg-red-500/10' },
  'admin.move_org_member': { label: 'Move org member', color: 'text-violet-400 bg-violet-500/10' },
  'comp.create': { label: 'Create comp', color: 'text-green-400 bg-green-500/10' },
  'org.add_member': { label: 'Add org member', color: 'text-green-400 bg-green-500/10' },
  'org.remove_member': { label: 'Remove org member', color: 'text-red-400 bg-red-500/10' },
  'org.update_member_role': { label: 'Update role', color: 'text-violet-400 bg-violet-500/10' },
};

const DEFAULT_META: ActionMeta = { color: 'text-gray-400 bg-gray-500/10', label: '' };

// namespace.verb_with_underscores -> "Verb with underscores" (title-cased,
// underscores turned to spaces). Used as the fallback for any action not in
// the explicit map above — covers all 28 (and future) discord.<command>
// audit actions from interactionHandler without hand-maintaining an
// exhaustive list that drifts every time a slash command is added.
function titleCaseVerb(verb: string): string {
  const spaced = verb.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function getActionMeta(action: string): ActionMeta {
  const known = ACTION_LABELS[action];
  if (known) return known;
  const dot = action.indexOf('.');
  if (dot === -1) return { ...DEFAULT_META, label: action };
  const namespace = action.slice(0, dot);
  const verb = action.slice(dot + 1);
  if (namespace === 'discord') {
    return { color: 'text-indigo-400 bg-indigo-500/10', label: `Discord: ${titleCaseVerb(verb)}` };
  }
  return { ...DEFAULT_META, label: titleCaseVerb(verb) };
}
